import { useState } from 'react';
import { AlertCircle } from 'lucide-react';

import { productionApi } from 'api/production';
import EditableList, { ListItemDetail } from 'components/storefront/StorefrontConfigEditor/components/EditableList';
import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { CONTENT_LINK_URL_PATTERN, CONTENT_LINK_URL_TITLE } from 'shared/utils/contentLinkUrl';

const DISPLAY_NAME = 'Banner Carousel 3';

/**
 * Default configuration for a banner item in the carousel
 *
 * @constant {Object}
 * @property {string} imageUrl - Image URL
 * @property {string} name - Banner name
 * @property {string} linkUrl - URL for the banner link
 */
const DEFAULT_BANNER_ITEM = Object.freeze({
    imageUrl: '',
    name: '',
    linkUrl: '',
});

/**
 * Default configuration for the BannerCarousel3 slice component
 *
 * @constant {Object}
 * @property {string} name - Name of the banner carousel
 * @property {Array} items - Array of banner items
 */
const DEFAULT_SLICE = Object.freeze({
    eyebrow: '',
    heading: '',
    name: '',
    items: [],
});

const MIN_BANNER_ITEMS = 5;

/**
 * Banner Carousel 3 slice component
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Function} props.onChange - Callback function for handling slice changes
 * @param {Object} props.slice - Slice configuration object
 * @returns {JSX.Element} BannerCarousel3 component
 */
function BannerCarousel3({ disabled, onChange, slice }) {
    const handleItemsChange = (updatedListItems) => {
        onChange({ ...slice, items: updatedListItems });
    };

    const handleNameChange = (e) => {
        onChange({ ...slice, name: e.target.value });
    };

    const handleEyebrowChange = (e) => {
        onChange({ ...slice, eyebrow: e.target.value });
    };

    const handleHeadingChange = (e) => {
        onChange({ ...slice, heading: e.target.value });
    };

    return (
        <div className="flex flex-col gap-6">
            {slice.items.length < MIN_BANNER_ITEMS && (
                <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>Warning</AlertTitle>
                    <AlertDescription>
                        A minimum of {MIN_BANNER_ITEMS} banner items are required for publication. Currently:{' '}
                        {slice.items.length}/{MIN_BANNER_ITEMS}
                    </AlertDescription>
                </Alert>
            )}

            <div className="flex flex-col gap-2">
                <Label htmlFor={`eyebrow-${slice.id}`}>Eyebrow</Label>
                <Input
                    type="text"
                    id={`eyebrow-${slice.id}`}
                    name="eyebrow"
                    value={slice.eyebrow ?? ''}
                    placeholder="Enter eyebrow"
                    onChange={handleEyebrowChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`heading-${slice.id}`}>Heading</Label>
                <Input
                    type="text"
                    id={`heading-${slice.id}`}
                    name="heading"
                    value={slice.heading ?? ''}
                    placeholder="Enter heading"
                    onChange={handleHeadingChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <Label htmlFor={`name-${slice.id}`}>Name</Label>
                <p className="text-xs text-muted-foreground -mt-0.5">
                    For internal reference only. Not displayed to customers.
                </p>
                <Input
                    type="text"
                    id={`name-${slice.id}`}
                    name="name"
                    value={slice.name ?? ''}
                    placeholder="Enter carousel name"
                    onChange={handleNameChange}
                    disabled={disabled}
                />
            </div>

            <EditableList
                defaultItem={DEFAULT_BANNER_ITEM}
                ListItemForm={ListItemForm}
                ListItemPreview={ListItemPreview}
                listItems={slice.items}
                onChange={handleItemsChange}
                slice={slice}
                disabled={disabled}
            />
        </div>
    );
}

/**
 * Form component for editing individual banner items in the carousel
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {number} props.index - Index of the current banner item
 * @param {Object} props.listItem - Current banner item data
 * @param {Function} props.onChange - Callback function for handling item changes
 * @param {Function} props.onInvalidInput - Callback function for handling invalid input
 * @param {Object} props.slice - Parent slice configuration object
 * @returns {JSX.Element} ListItemForm component
 */
function ListItemForm({ disabled, index, listItem, onChange, onInvalidInput, slice }) {
    const [imageError, setImageError] = useState(null);

    const handleChange = (e) => {
        onChange({ [e.target.name]: e.target.value });
    };

    const handleImageUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ imageUrl });
            setImageError(null);
        } catch (e) {
            setImageError(`Error: ${e.message}`);
        }
    };

    const handleClearImage = () => {
        onChange({ imageUrl: '' });
    };

    return (
        <>
            <ImageInput
                id={`image-${slice.id}-${index}`}
                imageUrl={listItem.imageUrl}
                error={imageError}
                onClearImage={handleClearImage}
                onImageUpload={handleImageUpload}
                onInvalid={onInvalidInput}
                disabled={disabled}
            />

            <div className="flex flex-col gap-2">
                <Label htmlFor={`name-${slice.id}-${index}`}>Name</Label>
                <Input
                    type="text"
                    id={`name-${slice.id}-${index}`}
                    name="name"
                    value={listItem.name ?? ''}
                    placeholder="Enter banner name"
                    onChange={handleChange}
                    required
                    onInvalid={onInvalidInput}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`linkUrl-${slice.id}-${index}`}>Link URL</Label>
                <Input
                    type="text"
                    id={`linkUrl-${slice.id}-${index}`}
                    name="linkUrl"
                    value={listItem.linkUrl ?? ''}
                    placeholder="Enter relative or absolute URL"
                    onChange={handleChange}
                    disabled={disabled}
                    required
                    pattern={CONTENT_LINK_URL_PATTERN}
                    title={CONTENT_LINK_URL_TITLE}
                />
            </div>
        </>
    );
}

/**
 * Preview component for displaying banner item details
 *
 * @param {Object} props
 * @param {Object} props.listItem - Banner item data to preview
 * @returns {JSX.Element} ListItemPreview component
 */
function ListItemPreview({ listItem }) {
    return (
        <>
            <ListItemDetail label="Image" value={listItem} type="image" />
            <ListItemDetail label="Name" value={listItem.name} />
            <ListItemDetail label="Link URL" value={listItem.linkUrl} />
        </>
    );
}

const validateSlice = (slice, position) => {
    const errors = [];

    if (slice.items.length < MIN_BANNER_ITEMS) {
        errors.push(`${DISPLAY_NAME} (position ${position}) requires at least ${MIN_BANNER_ITEMS} banner items.`);
    }

    if (slice.items.some((item) => !item.imageUrl)) {
        errors.push(`${DISPLAY_NAME} (position ${position}) has missing images.`);
    }

    if (slice.eyebrow && !slice.heading) {
        errors.push(`${DISPLAY_NAME} (position ${position}) requires a heading when an eyebrow is provided.`);
    }

    return errors;
};

export default Object.assign(BannerCarousel3, {
    category: 'banner',
    defaultSlice: DEFAULT_SLICE,
    description: 'A carousel consisting of clickable banners containing an image, name, and link.',
    displayName: DISPLAY_NAME,
    thumbnail: 'https://img-cdn.misfitsmarket.com/test/lettuce/Minimap-Thumbnail__Banner-Carousel-3__Desktop',
    displayType: 'BANNER_CAROUSEL_3',
    validateSlice,
});
